import { useState, useCallback, useMemo } from 'react'

interface UseReportBulkOperationsReturn {
  selectedFpCodes: Set<string>
  handleRowSelect: (fpCode: string) => void
  handleSelectAllVisible: (rows: Record<string, any>[]) => void
  clearSelection: () => void
  isFpSelected: (fpCode: string) => boolean
  isAllSelected: (rows: Record<string, any>[]) => boolean
  isIndeterminate: (rows: Record<string, any>[]) => boolean
  selectedCount: number
}

/**
 * Hook for managing bulk operations on platform report rows.
 *
 * Key design: Uses FP-based selection (not row-based) because:
 * - The same FP can appear in multiple rows (e.g., uncategorised products report)
 * - Bulk sync operates on FP codes, not individual rows
 * - Selecting any row selects that FP, highlighting all rows with same fp_code
 */
export const useReportBulkOperations = (): UseReportBulkOperationsReturn => {
  const [selectedFpCodes, setSelectedFpCodes] = useState<Set<string>>(new Set())

  const handleRowSelect = useCallback((fpCode: string) => {
    if (!fpCode) return
    setSelectedFpCodes(prev => {
      const newSelected = new Set(prev)
      if (newSelected.has(fpCode)) {
        newSelected.delete(fpCode)
      } else {
        newSelected.add(fpCode)
      }
      return newSelected
    })
  }, [])

  const handleSelectAllVisible = useCallback((rows: Record<string, any>[]) => {
    const visibleFpCodes = new Set(
      rows
        .map(row => row.fp_code as string)
        .filter(Boolean)
    )

    const allSelected = visibleFpCodes.size > 0 &&
      Array.from(visibleFpCodes).every(fpCode => selectedFpCodes.has(fpCode))

    if (allSelected) {
      // Deselect all visible FPs
      setSelectedFpCodes(prev => {
        const newSelected = new Set(prev)
        visibleFpCodes.forEach(fpCode => newSelected.delete(fpCode))
        return newSelected
      })
    } else {
      // Select all visible FPs (add to existing selection)
      setSelectedFpCodes(prev => {
        const newSelected = new Set(prev)
        visibleFpCodes.forEach(fpCode => newSelected.add(fpCode))
        return newSelected
      })
    }
  }, [selectedFpCodes])

  const clearSelection = useCallback(() => {
    setSelectedFpCodes(new Set())
  }, [])

  const isFpSelected = useCallback((fpCode: string) => {
    return selectedFpCodes.has(fpCode)
  }, [selectedFpCodes])

  const isAllSelected = useCallback((rows: Record<string, any>[]) => {
    const visibleFpCodes = new Set(
      rows
        .map(row => row.fp_code as string)
        .filter(Boolean)
    )
    return visibleFpCodes.size > 0 &&
      Array.from(visibleFpCodes).every(fpCode => selectedFpCodes.has(fpCode))
  }, [selectedFpCodes])

  const isIndeterminate = useCallback((rows: Record<string, any>[]) => {
    const visibleFpCodes = new Set(
      rows
        .map(row => row.fp_code as string)
        .filter(Boolean)
    )
    const someSelected = Array.from(visibleFpCodes).some(fpCode => selectedFpCodes.has(fpCode))
    const allSelected = visibleFpCodes.size > 0 &&
      Array.from(visibleFpCodes).every(fpCode => selectedFpCodes.has(fpCode))
    return someSelected && !allSelected
  }, [selectedFpCodes])

  const selectedCount = useMemo(() => selectedFpCodes.size, [selectedFpCodes])

  return {
    selectedFpCodes,
    handleRowSelect,
    handleSelectAllVisible,
    clearSelection,
    isFpSelected,
    isAllSelected,
    isIndeterminate,
    selectedCount,
  }
}
